import {
  DeviceTypeHelper,
  DeviceTypeIds,
  METER_1_PULSE_RESET_KEY,
  METER_2_PULSE_RESET_KEY,
  MeterConfig,
  RR4,
  RR4BLEInfoMessageType,
  RR4BLEMessageType,
  RR4Read,
  RRMeter,
  RRMeterInfo,
} from '@ncss/models/dist/v3';
import { BLE_SERVICES, DirectConnectBaseDevice, VisibleDevice } from '../base/directConnectBaseDevice';
import { ByteList } from 'byte-list';
import { RR4BLEDeviceMessage } from './messages/RR4BLEDeviceMessage';
import { shallowRef, ShallowRef } from 'vue';
import { RR4BLEInfoMessage } from './messages/RR4BLEInfoMessage';
import { computeMeterOverallStatus, OverallStatus } from '../base/utils';

export interface RR4Command {
  rapidCheckInEnabled?: boolean;
}

export class DirectConnectRR4 extends DirectConnectBaseDevice {
  static Init() {
    DirectConnectBaseDevice.RegisterType(DeviceTypeIds.RR4, DirectConnectRR4);
    DirectConnectBaseDevice.RegisterType(DeviceTypeIds.RR4_TR, DirectConnectRR4);
  }

  temperature?: number;
  secondsSinceUserMagnet?: number;
  rawRfSignal?: number;

  get info() {
    return this._info.value;
  }
  get read() {
    return this._read.value;
  }
  get loading() {
    return this._loading.value;
  }
  get linkQualityStr() {
    return this._linkQualityStr.value;
  }
  get status() {
    return this._status.value;
  }
  get isTRDevice() {
    return this._isTRDevice;
  }

  protected serviceId = BLE_SERVICES.WALK_BY.ID;
  protected notifyCharacteristicId = BLE_SERVICES.WALK_BY.CHARACTERISTICS.NOTIFY;
  protected writeCharacteristicId = BLE_SERVICES.WALK_BY.CHARACTERISTICS.WRITE;

  private _advertisedFlags?: number;
  private _info: ShallowRef<RR4 | undefined>;
  private _read: ShallowRef<RR4Read | undefined>;
  private _loading: ShallowRef<boolean>;
  private _linkQualityStr: ShallowRef<string | undefined>;
  private _status: ShallowRef<OverallStatus | undefined>;
  private _isTRDevice: boolean;

  constructor(visibleDevice: VisibleDevice, useMetric: boolean) {
    super(visibleDevice, useMetric);
    this._info = shallowRef();
    this._read = shallowRef();
    this._loading = shallowRef(false);
    this._linkQualityStr = shallowRef();
    this._status = shallowRef();
    const deviceType = DeviceTypeHelper.GetDeviceTypeBySerialNumber(this.deviceId);
    this._isTRDevice = !!deviceType && deviceType.id === DeviceTypeIds.RR4_TR;
  }

  setRapidCheckIn(enabled: boolean) {
    const msg = new RR4BLEInfoMessage(RR4BLEInfoMessageType.INFO_MSG, this.deviceId);
    if (enabled) {
      msg.rr4Info.startRapidCheckIn = true;
    } else {
      msg.rr4Info.stopRapidCheckIn = true;
    }
    return this.sendMessage(msg, {
      match: (res) => res instanceof RR4BLEDeviceMessage && res.rr4?.rapidCheckInEnabled === enabled,
    }).then((res) => !!res);
  }

  setLcdAlwaysOn(enabled: boolean) {
    const msg = new RR4BLEInfoMessage(RR4BLEInfoMessageType.INFO_MSG, this.deviceId);
    msg.rr4Info.setLCDAlwaysOnState = true;
    msg.rr4Info.lcdAlwaysOn = enabled;
    return this.sendMessage(msg, {
      match: (res) => res instanceof RR4BLEDeviceMessage && res.rr4?.lcdAlwaysOn === enabled,
    }).then((res) => !!res);
  }

  setSwapCommasToDecimals(enabled: boolean) {
    const msg = new RR4BLEInfoMessage(RR4BLEInfoMessageType.INFO_MSG, this.deviceId);
    msg.rr4Info.setLcdSwapCommasAndDecimals = true;
    msg.rr4Info.lcdSwapCommasAndDecimals = enabled;
    return this.sendMessage(msg, {
      match: (res) => res instanceof RR4BLEDeviceMessage && res.rr4?.lcdSwapCommasAndDecimals === enabled,
    }).then((res) => !!res);
  }

  setShowTamperAlerts(enabled: boolean) {
    const msg = new RR4BLEInfoMessage(RR4BLEInfoMessageType.INFO_MSG, this.deviceId);
    msg.rr4Info.setTamperAlertState = true;
    msg.rr4Info.tamperAlertEnableState = enabled;
    return this.sendMessage(msg, {
      match: (res) => res instanceof RR4BLEDeviceMessage && res.rr4?.tamperAlertEnabled === enabled,
    }).then((res) => !!res);
  }

  clearTamperAlert() {
    const msg = new RR4BLEInfoMessage(RR4BLEInfoMessageType.INFO_MSG, this.deviceId);
    msg.rr4Info.clearTamperAlert = true;
    return this.sendMessage(msg, {
      match: (res) => res instanceof RR4BLEDeviceMessage && res.rr4?.tamperAlert === false,
    }).then((res) => !!res);
  }

  updateMeter(info: RRMeterInfo, meterNumber: 1 | 2) {
    const msg = new RR4BLEInfoMessage(RR4BLEInfoMessageType.INFO_MSG, this.deviceId);
    if (meterNumber === 1) {
      msg.rr4Info.meter1 = info;
      if (info.resetPulseCount) {
        msg.rr4Info.key ^= METER_1_PULSE_RESET_KEY;
      }
      if (info.configType !== MeterConfig.PULSE_IN && this.info?.meter2?.configType === MeterConfig.PULSE_OUT) {
        msg.rr4Info.meter2 = this.getStartingMeterInfo(this.info.meter2);
        msg.rr4Info.meter2.configType = MeterConfig.PORT_DISABLED;
        msg.rr4Info.meter2.setInfo = true;
      }
    } else {
      msg.rr4Info.meter2 = info;
      if (info.resetPulseCount) {
        msg.rr4Info.key ^= METER_2_PULSE_RESET_KEY;
      }
    }
    return this.sendMessage(msg, {
      match: (res) => res instanceof RR4BLEDeviceMessage,
    }).then((res) => !!res);
  }

  getStartingMeterInfo(meter: RRMeter) {
    const info = new RRMeterInfo();
    info.configType = meter.configType;
    info.highVoltMode = meter.highVoltMode;
    info.utilityTypeId = meter.utilityTypeId;
    info.uomTypeId = meter.uomTypeId;
    info.resetPulseCount = false;
    info.rawMultiplier = meter.getRawMultiplier();
    info.imr = meter.imr;
    info.lcdDecimalPositionEn = meter.lcdDecimalPositionEn;
    info.lcdDecimalPosition = meter.lcdDecimalPosition;
    return info;
  }

  protected parseManufacturerData(bytes: ByteList): void {
    this.temperature = bytes.readInt8();
    this.secondsSinceUserMagnet = bytes.readByte();
    this.rawRfSignal = bytes.readByte();
    this._advertisedFlags = bytes.readByte();
  }

  protected registerMessages(): void {
    this.messageService.registerMessageGroup({
      mask: RR4BLEMessageType.DEVICE_MSG,
      messageClass: RR4BLEDeviceMessage,
      handler: this.onDeviceMessage.bind(this),
    });
  }

  protected onConnected() {
    super.onConnected();
    this._loading.value = true;
  }

  private onDeviceMessage(msg: RR4BLEDeviceMessage) {
    this._loading.value = false;
    if (!msg.rr4) {
      return;
    }
    console.log('DEVICE MESSAGE', msg);
    this._read.value = RR4Read.Create(this.deviceId, msg.rr4, {
      firstHopId: 0,
      firstHopRssi: 0,
      isProgrammed: false,
      lastHopRssi: 0,
    }).filter((r) => !r.isHistoricalRead)?.[0];
    this.setInfo(msg.rr4);
    this.checkResponse(msg);
  }

  private setInfo(rr4: RR4) {
    this._info.value = rr4;
    this._linkQualityStr.value = this.computeLinkQualityStr(rr4.rfSignal);
    this._status.value = this.computeOverallStatus(rr4);
  }

  private computeOverallStatus(rr4: RR4): { isOk: boolean; description: string; label: string } {
    const status: OverallStatus = {
      isOk: true,
      label: '',
      description: '',
    };
    if (this.isTRDevice) {
      status.isOk = rr4.gatewayReplied && this.linkQualityStr !== 'Poor';
      status.label =
        this.linkQualityStr === 'Poor'
          ? 'Poor Transceiver Signal'
          : rr4.gatewayReplied
          ? 'All Good'
          : 'Not Communicating With Gateway';
      status.description = rr4.gatewayReplied
        ? `This device has a${this.linkQualityStr === 'Excellent' ? 'n' : ''} ${
            this.linkQualityStr
          } connection to the Gateway.`
        : `This Remote Reader has not received a response from any nearby Gateways. This could mean that this device is either not programmed to a Unit yet or it is not within range of a Gateway.`;
    }

    const meterStatus = computeMeterOverallStatus(
      { type: rr4.meter1.configType, isOk: rr4.meter1Health, protocol: rr4.meter1.encoderProtocol },
      { type: rr4.meter2.configType, isOk: rr4.meter2Health, protocol: rr4.meter2.encoderProtocol }
    );
    if (meterStatus) {
      status.meter1 = meterStatus.meter1;
      status.meter2 = meterStatus.meter2;
      if (meterStatus.isOk) {
        status.description += ' ' + meterStatus.description;
      } else {
        status.isOk = false;
        status.checkWiring = meterStatus.checkWiring;
        status.label = meterStatus.label;
        status.description = meterStatus.description;
      }
    }

    return status;
  }

  private computeLinkQualityStr(rfSignal: number) {
    switch (rfSignal) {
      case 4:
        return 'Excellent';
      case 3:
      case 2:
        return 'Good';
      case 1:
        return 'Poor';
      default:
        return '';
    }
  }
}
